import { DownloadIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { ContentEditor, type Json } from '../ui/ContentEditor';
import { CopyButton } from './CopyButton';
import { useAdmin } from '../AdminContext';
import { LOGO } from '../../data/content';
import { palettes } from './marketingOptions';
import { btnSecondary, inputCls } from '../ui/styles';
import { fillTokens, marketingTokens } from './marketingRules';
import type { BrandKit as Kit } from '../../core/types/marketing';

/** Instagram staat in de bio niet meer tekens toe. */
const INSTAGRAM_BIO_MAX = 150;

/** Merkstijl en kant-en-klare teksten: alles om zelf consequent te communiceren. Tabblad van /be/ontwerpen. */
export function BrandKit() {
  const {
    marketing,
    setMarketing,
    site
  } = useAdmin();
  const kit = marketing.brandKit;
  const tokens = marketingTokens(site);
  const set = <K extends keyof Kit,>(key: K, value: Kit[K]) => setMarketing(m => ({
    ...m,
    brandKit: {
      ...m.brandKit,
      [key]: value
    }
  }));
  // ContentEditor werkt op vrije JSON; de merkstijl-velden hebben een vaste vorm. De editor
  // bewaart die vorm (hij voegt alleen rijen toe met dezelfde velden), vandaar de omzetting.
  const edit = <K extends keyof Kit,>(key: K) => ({
    fieldKey: key,
    value: kit[key] as unknown as Json,
    onChange: (v: Json) => set(key, v as unknown as Kit[K])
  });
  const setBio = (i: number, patch: Partial<Kit['bios'][number]>) => set('bios', kit.bios.map((x, j) => j === i ? {
    ...x,
    ...patch
  } : x));
  return <div data-id="element-2893" data-mp="153vejj6u9t1x">
      <div className="space-y-6" data-id="element-2894" data-mp="150zffrj98ybw">
        <Panel title="Kleuren" description="Klik op een code om hem te kopiëren. Geen verlopen: altijd effen vlakken." data-id="element-2895" data-mp="103qz7who34da">
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-id="element-2896" data-mp="v3urbm5umssg">
            {palettes.map(p => <li key={p.key} className="overflow-hidden rounded-md ring-1 ring-line" data-id="element-2897" data-mp="3kv187y0rw73b">
                <div className="flex h-24 items-end justify-between p-3" style={{
              background: p.bg
            }} aria-hidden="true" data-id="element-2898" data-mp="3fu3r7ori1j3e">
                  <span className="font-display text-[26px] leading-none" style={{
                color: p.fg
              }} data-id="element-2899" data-mp="3hd3na6zemnkv">
                    Aa
                  </span>
                  <span className="h-4 w-4 rounded-full" style={{
                background: p.accent
              }} data-id="element-2900" data-mp="38c5hpk9bi806" />
                </div>
                <div className="bg-white px-3 py-2" data-id="element-2901" data-mp="3d9xjd5zrl8hk">
                  <p className="text-[13px] text-ink" data-id="element-2902" data-mp="3dctfqpvyrp3w">{p.name}</p>
                  <div className="-mx-2.5 flex flex-wrap" data-id="element-2903" data-mp="2yl15gsb11191">
                    <CopyButton text={p.bg} label={p.bg} data-id="element-2904" data-mp="33c9fnqk6kqn9" />
                  </div>
                </div>
              </li>)}
          </ul>
        </Panel>

        <div className="grid gap-6 lg:grid-cols-2" data-id="element-2905" data-mp="2pj1m3tcjhl2x">
          <Panel title="Letters" data-id="element-2906" data-mp="2tlybq40hyhgd">
            <div className="space-y-6" data-id="element-2907" data-mp="2tmzm069ouzx1">
              <div data-id="element-2908" data-mp="2fn8d2jy149pw">
                <p className="font-display text-[40px] font-light leading-none text-ink" data-id="element-2909" data-mp="2fmot6xotjb98">
                  Fraunces <span className="italic text-clay" data-id="element-2910" data-mp="2kmb95ew175lq">cursief</span>
                </p>
                <p className="mt-2 text-[13px] text-muted" data-id="element-2911" data-mp="20zq4vbl9cwyq">Koppen en citaten. Dun (300), het cursieve deel in de accentkleur.</p>
              </div>
              <div className="border-t border-line pt-5" data-id="element-2912" data-mp="1w2zn8jtt1tb7">
                <p className="text-[22px] text-ink" data-id="element-2913" data-mp="2aypdiu60wslq">Inter — voor alle lopende tekst</p>
                <p className="mt-2 text-[13px] text-muted" data-id="element-2914" data-mp="25ytb2zcz598a">Tekst, knoppen, prijzen en kleine regels. Nooit in hoofdletters.</p>
              </div>
              <p className="text-[13px] text-muted" data-id="element-2915" data-mp="2jxj3w2srgmqu">
                Beide gratis te downloaden via{' '}
                <a href="https://fonts.google.com/share?selection.family=Fraunces:ital,wght@0,300;1,300|Inter:wght@300;400;500" target="_blank" rel="noreferrer" className="text-ink underline underline-offset-2" data-id="element-2916" data-mp="2ezr6it9ck8f9">
                  Google Fonts
                  <span className="sr-only" data-id="element-2917" data-mp="2yo9z7ktka3yp"> (opent in een nieuw tabblad)</span>
                </a>
                .
              </p>
            </div>
          </Panel>

          <Panel title="Logo" data-id="element-2918" data-mp="vz8ol8j9323o">
            <div className="flex items-center justify-center rounded-md bg-white p-8 ring-1 ring-line" data-id="element-2919" data-mp="11064tfkp9m2j">
              <img src={LOGO} alt="Beauty Bewust logo" className="h-16 w-auto" data-id="element-2920" data-mp="15rebi3unatc0" />
            </div>
            <p className="mt-4 text-[13px] leading-relaxed text-muted" data-id="element-2921" data-mp="1apzda7pjpl2k">
              Op lichte vlakken het logo. Op gekleurde of donkere vlakken gebruiken de ontwerpen het woordmerk in Fraunces: dat blijft
              overal scherp. Houd rondom minstens de hoogte van de B vrij.
            </p>
            <a href={LOGO} download="beautybewust-logo.jpg" target="_blank" rel="noreferrer" className={`${btnSecondary} mt-4`} data-id="element-2922" data-mp="n0wnudoo6nxi">
              <DownloadIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-2923" data-mp="r3baz9zdg9ul" />
              Download logo
            </a>
          </Panel>
        </div>

        <Panel title="Waar Beauty Bewust voor staat" description="Vier boodschappen. Elk bericht draagt er minstens één." data-id="element-2924" data-mp="1yi66ixzz5bxr">
          <ul className="mb-6 grid gap-x-8 gap-y-5 sm:grid-cols-2" data-id="element-2925" data-mp="3mz7pj5hbkxq9">
            {kit.messages.map((m, i) => <li key={i} className="border-t border-line pt-4" data-id="element-2926" data-mp="3ixm4uxyjnf5i" data-mpi={i}>
                <p className="font-display text-[20px] text-ink" data-id="element-2927" data-mp="3j08f55ctk8rf">{m.title}</p>
                <p className="mt-1 text-[14px] leading-relaxed text-muted" data-id="element-2928" data-mp="3e5b8g530ibhp">{fillTokens(m.text, tokens)}</p>
              </li>)}
          </ul>
          <details className="group" data-id="element-2929" data-mp="34985ix0c68tp">
            <summary className="cursor-pointer text-[13px] text-clay" data-id="element-2930" data-mp="34a9hd3p6ezch">Kernboodschappen aanpassen</summary>
            <div className="mt-4" data-id="element-2931" data-mp="2z8k6pp5v8nox">
              <ContentEditor {...edit('messages')} data-id="element-2932" data-mp="2v8062bm331y1" />
            </div>
          </details>
        </Panel>

        <div className="grid gap-6 lg:grid-cols-2" data-id="element-2933" data-mp="2q9qbo386o5dh">
          <Panel title="Slogans" data-id="element-2934" data-mp="2qb1cpva8uz0w">
            <ContentEditor {...edit('taglines')} data-id="element-2935" data-mp="tgn49vfexa67" />
          </Panel>
          <Panel title="Hashtags" description="Deze staan ook als suggestie bij elk ontwerp." data-id="element-2936" data-mp="teu7r8yik6bx">
            <ContentEditor {...edit('hashtags')} data-id="element-2937" data-mp="oiliv3uikyrk" />
            <div className="mt-3 flex justify-end" data-id="element-2938" data-mp="17lx9znpwag5j">
              <CopyButton text={kit.hashtags.join(' ')} label="Kopieer alle" data-id="element-2939" data-mp="17kcb5lwjsb1v" />
            </div>
          </Panel>
        </div>

        <Panel title="Zo klinkt het" data-id="element-2940" data-mp="ym0o1tuw3ve6">
          <div className="grid gap-8 sm:grid-cols-2" data-id="element-2941" data-mp="yn1zw0jqclwy">
            <div data-id="element-2942" data-mp="13kv6uvsuxjya">
              <p className="mb-3 text-[14px] font-medium text-sage" data-id="element-2943" data-mp="13kbyo6t0e9xg">Wel doen</p>
              <ContentEditor {...edit('toneDo')} data-id="element-2944" data-mp="1rafye332tpj6" />
            </div>
            <div data-id="element-2945" data-mp="1rb7j2iv4y2uo">
              <p className="mb-3 text-[14px] font-medium text-clay" data-id="element-2946" data-mp="1r8lgyyr0kfju">Niet doen</p>
              <ContentEditor {...edit('toneDont')} data-id="element-2947" data-mp="1vb88sznoq0h1" />
            </div>
          </div>
        </Panel>

        <Panel title="Kant-en-klare teksten" description="Kopieer en plak: {tokens} zijn al ingevuld met je instellingen." data-id="element-2948" data-mp="1hecouc5waom7">
          <ul className="space-y-5" data-id="element-2949" data-mp="5x37bv6bu6nj">
            {kit.bios.map((b, i) => {
            const filled = fillTokens(b.text, tokens);
            return <li key={i} className="rounded-md border border-line bg-shell/60 p-4" data-id="element-2950" data-mp="axr6j44pltsu" data-mpi={i}>
                  <div className="mb-2 flex items-center justify-between gap-3" data-id="element-2951" data-mp="3bc5pp03qh4w">
                    <input value={b.label} onChange={e => setBio(i, {
                  label: e.target.value
                })} aria-label="Naam van de tekst" className="min-w-0 flex-1 bg-transparent text-[14px] font-medium text-ink focus:outline-none" data-id="element-2952" data-mp="3ujrj8rh2tp84" />
                    <CopyButton text={filled} data-id="element-2953" data-mp="3fubt9dpddnth" />
                  </div>
                  <Field label="Tekst" count={b.label.toLowerCase().includes('instagram') ? {
                value: filled.length,
                max: INSTAGRAM_BIO_MAX
              } : undefined} data-id="element-2954" data-mp="3bti30dogfwxf">
                    <textarea value={b.text} onChange={e => setBio(i, {
                  text: e.target.value
                })} rows={Math.max(3, b.text.split('\n').length + 1)} className={`${inputCls} resize-y leading-relaxed`} data-id="element-2955" data-mp="3krlqhfj033rv" />
                  </Field>
                  {filled !== b.text && <p className="mt-2 whitespace-pre-line text-[13px] leading-relaxed text-muted" data-id="element-2956" data-mp="2blapbdtd7kl8">
                      <span className="text-sage" data-id="element-2957" data-mp="2giavrpu4mpcv">Zo wordt het:</span> {filled}
                    </p>}
                </li>;
          })}
          </ul>
        </Panel>

        <Panel title="Een rustig ritme" description="Liever drie goede berichten per week dan elke dag iets. Past bij hoe je werkt." data-id="element-2958" data-mp="2qggy28juhxi9">
          <ol className="mb-6 divide-y divide-line" data-id="element-2959" data-mp="2uhai2qu62s5v">
            {kit.rhythm.map((r, i) => <li key={i} className="flex flex-col gap-1 py-3 sm:flex-row sm:gap-6" data-id="element-2960" data-mp="2zalsf1y6tr2j" data-mpi={i}>
                <span className="w-44 shrink-0 text-[14px] text-ink" data-id="element-2961" data-mp="348ezryb88r4e">{r.moment}</span>
                <span className="text-[14px] text-muted" data-id="element-2962" data-mp="399b247fxk57p">{r.what}</span>
              </li>)}
          </ol>
          <details data-id="element-2963" data-mp="3e7meopxm0ttm">
            <summary className="cursor-pointer text-[13px] text-clay" data-id="element-2964" data-mp="3e4qe0tudnr1h">Ritme aanpassen</summary>
            <div className="mt-4" data-id="element-2965" data-mp="12ho99fynw258">
              <ContentEditor {...edit('rhythm')} data-id="element-2966" data-mp="xk4u8du53lc8" />
            </div>
          </details>
        </Panel>
      </div>
    </div>;
}